import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Loader2, GraduationCap, FileText, Award } from "lucide-react";

const STATUS_LABEL = { graded: "Corrigé", submitted: "Soumis", todo: "À faire" };

export function LmsTranscript() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/lms/my-transcript").then(({ data }) => setData(data)).catch(() => setData({ transcript: [] }));
  }, []);

  const openPdf = async (url, filename) => {
    setBusy(true);
    try {
      const res = await api.get(url, { responseType: "blob" });
      const objUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = objUrl; a.download = filename; a.click();
      URL.revokeObjectURL(objUrl);
    } catch (e) { toast.error(apiError(e, "Document indisponible")); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-ciel" size={26} /></div>;
  const { transcript } = data;

  if (!transcript.length) {
    return (
      <p className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="no-formations">
        Vous n'êtes inscrit·e à aucune formation pour le moment.
      </p>
    );
  }

  return (
    <div data-testid="lms-transcript">
      <div className="mb-4 flex items-center justify-end">
        <Button onClick={() => openPdf("/lms/my-transcript.pdf", "bulletin-de-notes.pdf")} disabled={busy}
          data-testid="transcript-pdf-btn" className="rounded-full bg-navy text-xs text-white hover:bg-navy-light">
          {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <FileText size={13} className="mr-1" />} Bulletin PDF
        </Button>
      </div>
      <div className="space-y-5">
        {transcript.map((t) => (
          <div key={t.course_id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`transcript-course-${t.course_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="flex items-center gap-2 font-display text-lg font-bold text-navy"><GraduationCap size={18} className="text-[#0E7C7B]" /> {t.title}</p>
                <p className="mt-0.5 text-xs text-slate-500">{t.instructor ? `${t.instructor} · ` : ""}{t.category}</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${t.completed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {t.completed ? "Terminé" : `En cours · ${t.progress}%`}
              </span>
            </div>

            <div className="mt-3 h-2 w-full rounded-full bg-slate-100"><div className="h-2 rounded-full bg-[#0E7C7B]" style={{ width: `${t.progress}%` }} /></div>

            {(t.assignments.length > 0 || t.quizzes.length > 0) ? (
              <table className="mt-4 w-full text-left text-xs">
                <thead className="text-slate-400"><tr><th className="py-1">Type</th><th>Évaluation</th><th>Résultat</th><th>Statut</th></tr></thead>
                <tbody>
                  {t.assignments.map((a, i) => (
                    <tr key={`a${i}`} className="border-t border-slate-100" data-testid={`transcript-assign-${t.course_id}-${i}`}>
                      <td className="py-1.5 text-slate-500">Devoir</td>
                      <td className="text-navy">{a.title}</td>
                      <td className="font-semibold text-[#0E7C7B]">{a.grade != null ? `${a.grade}/${a.max_grade}` : "—"}</td>
                      <td className="text-slate-500">{STATUS_LABEL[a.status] || a.status}</td>
                    </tr>
                  ))}
                  {t.quizzes.map((q, i) => (
                    <tr key={`q${i}`} className="border-t border-slate-100" data-testid={`transcript-quiz-${t.course_id}-${i}`}>
                      <td className="py-1.5 text-slate-500">Composition</td>
                      <td className="text-navy">{q.title}</td>
                      <td className="font-semibold text-[#0E7C7B]">{q.best_score != null ? `${q.best_score}%` : "—"}</td>
                      <td className={q.passed ? "font-semibold text-emerald-600" : "text-slate-500"}>{q.passed ? "Réussi" : q.best_score != null ? "Non réussi" : "Non passé"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="mt-3 text-xs text-slate-400">Aucune évaluation pour ce cours.</p>}

            {t.certificate_available && (
              <Button onClick={() => openPdf(`/lms/courses/${t.course_id}/certificate.pdf`, `certificat-${t.title}.pdf`)} disabled={busy}
                data-testid={`transcript-cert-${t.course_id}`} className="mt-4 rounded-full bg-[#0E7C7B] text-xs text-white hover:bg-[#0b6463]">
                <Award size={14} className="mr-1" /> Certificat de participation
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
